
import React from 'react';
import { 
    LayoutDashboard, Users, Newspaper, ShieldAlert, Settings, 
    LogOut, ShieldCheck, X, ChevronRight, Megaphone, LayoutPanelLeft,
    MessageSquare, Video, Trophy, Brain, HelpCircle
} from 'lucide-react';
import { XlevenLogo } from '../Common/XlevenLogo';
import { useLanguage } from '../../context/LanguageContext';
import { at } from '../../i18nAdmin';

export type AdminTab = 'overview' | 'banners' | 'users' | 'players' | 'matchAnalysis' | 'content' | 'highlights' | 'shorts' | 'gameStore' | 'footyMaster' | 'toto' | 'inquiries' | 'notices' | 'reports' | 'settings';

interface AdminSidebarProps {
    activeTab: AdminTab;
    setActiveTab: (tab: AdminTab) => void;
    onExit: () => void;
    isOpen: boolean;
    onClose: () => void;
}

export const AdminSidebar: React.FC<AdminSidebarProps> = ({ 
    activeTab, setActiveTab, onExit, isOpen, onClose 
}) => {
    const { lang } = useLanguage();
    
    const navItems = [
        { id: 'overview', label: at(lang, 'overview'), icon: LayoutDashboard },
        { id: 'banners', label: '배너 관리', icon: Megaphone },
        { id: 'users', label: at(lang, 'users'), icon: Users },
        { id: 'players', label: at(lang, 'players'), icon: Trophy },
        { id: 'matchAnalysis', label: '경기 분석', icon: Brain },
        { id: 'content', label: at(lang, 'content'), icon: Newspaper },
        { id: 'highlights', label: at(lang, 'highlights'), icon: Video },
        { id: 'shorts', label: at(lang, 'shorts'), icon: Video },
        { id: 'gameStore', label: at(lang, 'gameStore'), icon: LayoutPanelLeft },
        { id: 'footyMaster', label: at(lang, 'footyMaster'), icon: HelpCircle },
        { id: 'toto', label: '축구 토토', icon: Trophy },
        { id: 'inquiries', label: at(lang, 'inquiries'), icon: MessageSquare },
        { id: 'notices', label: at(lang, 'notices'), icon: Megaphone },
        { id: 'reports', label: at(lang, 'securityAlerts'), icon: ShieldAlert },
        { id: 'settings', label: at(lang, 'settings'), icon: Settings },
    ];

    return (
        <>
            <div 
                className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-[210] transition-opacity duration-300 lg:hidden ${
                    isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                }`}
                onClick={onClose}
            />

            <aside className={`fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 flex flex-col z-[220] transition-transform duration-500 lg:static lg:translate-x-0 ${
                isOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
                <div className="p-6 h-[70px] flex items-center justify-between border-b border-slate-100">
                    <XlevenLogo />
                    <button onClick={onClose} className="lg:hidden p-2 text-slate-400 md:hover:text-slate-900 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 px-3 py-4 space-y-1 overflow-y-auto custom-scrollbar">
                    <p className="px-4 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest">{at(lang, 'adminControl')}</p>
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => {
                                setActiveTab(item.id as AdminTab);
                                if (window.innerWidth < 1024) onClose();
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all text-left group ${
                                activeTab === item.id 
                                ? 'bg-slate-900 text-white shadow-md font-bold' 
                                : 'text-slate-600 md:hover:bg-slate-50 md:hover:text-slate-900'
                            }`}
                        >
                            <item.icon size={18} strokeWidth={activeTab === item.id ? 2.5 : 2} className={activeTab === item.id ? 'text-rose-500' : 'text-slate-400 md:group-hover:text-slate-900'} />
                            <span className="text-sm font-medium tracking-tight">{item.label}</span>
                            {activeTab === item.id && <ChevronRight size={14} className="ml-auto opacity-50" />}
                        </button>
                    ))}
                </div>

                <div className="p-4 mt-auto border-t border-slate-100 bg-slate-50/30">
                    <button 
                        onClick={onExit}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 md:hover:bg-white md:hover:text-rose-600 md:hover:shadow-sm transition-all font-bold text-xs uppercase tracking-widest"
                    >
                        <LogOut size={18} />
                        {at(lang, 'exitConsole')}
                    </button>
                </div>
            </aside>
        </>
    );
};
